import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useLessonStore } from '@/store/lessonStore'
import { webSocketService } from '@/services/websocket'
import { apiClient } from '@/services/api'
import { Maximize2, Minimize2, ChevronLeft, ChevronRight, Monitor, Trophy, Users, Eye, SkipForward, Square, Loader2 } from 'lucide-react'
import { ShellActivity } from '@/components/player/ShellActivity'
import { BlocklyActivity } from '@/components/blockly/BlocklyActivity'
import { PythonReplActivity } from '@/components/player/PythonReplActivity'
import { PotatoPiratesActivity } from '@/components/potatoPirates/PotatoPiratesActivity'
import { resolvePhishingAnswer } from '@/utils/phishing'

const QUIZ_TYPES = ['mcq', 'cyber_hygiene_quiz', 'coding_quiz', 'packet_analysis', 'phishing_quiz', 'command_shell']

const DIFFICULTY_CLASSES: Record<string, string> = {
  easy: 'bg-[var(--color-success-subtle)] text-[var(--color-success)]',
  medium: 'bg-[var(--color-warning-subtle)] text-[var(--color-warning)]',
  hard: 'bg-[var(--color-danger-subtle)] text-[var(--color-danger)]',
}

const DifficultyBadge = ({ difficulty }: { difficulty: string }) => (
  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${DIFFICULTY_CLASSES[difficulty] || ''}`}>
    {difficulty}
  </span>
)

interface QuestionDisplayProps {
  /** Whether advance controls should be shown (mirrors the top "Next Activity" button visibility) */
  showAdvanceControls?: boolean
  /** True when there is a next pending activity to advance to */
  hasNextActivity?: boolean
  /** True while an advance/end request is in flight */
  advancing?: boolean
  /** Ends the current activity and starts the next one */
  onNextActivity?: () => void
  /** Ends the current activity without starting another (no next activity) */
  onEndActivity?: () => void
}

export function QuestionDisplay({
  showAdvanceControls = false,
  hasNextActivity = false,
  advancing = false,
  onNextActivity,
  onEndActivity,
}: QuestionDisplayProps = {}) {
  const { currentLesson, currentActivity, currentSlide, responses, players } = useLessonStore()
  const containerRef = useRef<HTMLDivElement>(null)
  // Initialize from the live fullscreen state so a remount (e.g. during an
  // activity transition) doesn't flash the non-fullscreen layout.
  const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement)
  const [viewMode, setViewMode] = useState<'question' | 'leaderboard'>('question')

  // Reset viewMode when activity changes
  useEffect(() => {
    setViewMode('question')
  }, [currentActivity?.id])

  // Determine if answers should be revealed
  const shouldReveal = useMemo(() => {
    if (!currentActivity) return false
    // Reveal if activity is no longer active (completed, paused, etc.)
    if (currentActivity.status !== 'active') return true
    // Reveal if all connected non-host players have responded
    const connectedStudents = players.filter(p => !p.is_host && p.connection_status === 'connected')
    return connectedStudents.length > 0 && responses.length >= connectedStudents.length
  }, [currentActivity, players, responses])

  const isQuizType = currentActivity ? QUIZ_TYPES.includes(currentActivity.activity_type) : false

  // Response count for the badge
  const responseInfo = useMemo(() => {
    const connectedStudents = players.filter(p => !p.is_host && p.connection_status === 'connected')
    return { responded: responses.length, total: connectedStudents.length }
  }, [players, responses])

  // Sync isFullscreen state when the user presses Esc or the browser exits fullscreen
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  // Arrow-key slide navigation in fullscreen for PDF activities
  useEffect(() => {
    if (!isFullscreen || currentActivity?.activity_type !== 'pdf_slideshow') return

    const totalSlides = currentActivity.activity_data?.total_slides ?? 1

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        handleChangeSlide(currentSlide + 1, totalSlides)
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        handleChangeSlide(currentSlide - 1, totalSlides)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isFullscreen, currentActivity, currentSlide])

  const handleToggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        // Fullscreen the root element (not this component's container) so
        // fullscreen survives activity transitions that briefly unmount this
        // component — it only exits when the host explicitly toggles it or Esc.
        await document.documentElement.requestFullscreen()
      } catch {
        // fullscreen not available
      }
    } else {
      document.exitFullscreen()
    }
  }

  const handleChangeSlide = (page: number, total?: number) => {
    if (!currentLesson?.id || !currentActivity?.id) return
    const totalSlides = total ?? currentActivity.activity_data?.total_slides ?? 1
    if (page < 1 || page > totalSlides) return
    useLessonStore.getState().setCurrentSlide(page)
    webSocketService.performHostAction(currentLesson.id, 'change_slide', {
      activity_id: currentActivity.id,
      page,
    })
    apiClient.updateActivity(currentLesson.id, currentActivity.id, {
      activity_data: { ...currentActivity.activity_data, current_slide: page },
    } as any).catch(() => {/* best-effort */})
  }

  const renderLeaderboard = () => {
    const ranked = [...players]
      .filter(p => !p.is_host)
      .sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0) || b.response_count - a.response_count)

    const medalColors = ['text-[var(--color-warning)]', 'text-theme-text', 'text-[var(--color-secondary)]']
    const bigText = isFullscreen ? 'text-4xl' : 'text-2xl'

    return (
      <div className="max-w-2xl mx-auto w-full px-6 py-8">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Trophy className={`w-8 h-8 text-[var(--color-warning)]`} />
          <h2 className={`font-bold ${bigText}`}>Leaderboard</h2>
        </div>

        <div className="space-y-2">
          {ranked.slice(0, 10).map((player, index) => (
            <div
              key={player.id}
              className={`flex items-center gap-4 px-6 py-4 rounded-xl border transition-all ${
                index === 0
                  ? 'bg-[var(--color-warning-subtle)] border-[var(--color-warning)]/40'
                  : index === 1
                  ? 'bg-surface-hover/30 border-gray-400/30'
                  : index === 2
                  ? 'bg-[var(--color-secondary-subtle)] border-[var(--color-secondary)]/30'
                  : 'bg-surface-deep/50 border-theme-border'
              }`}
            >
              <div className={`text-2xl font-bold w-10 text-center shrink-0 ${
                index < 3 ? medalColors[index] : 'text-theme-muted'
              }`}>
                {index + 1}
              </div>

              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shrink-0 ${
                index === 0 ? 'bg-[var(--color-warning)]' :
                index === 1 ? 'bg-gray-500' :
                index === 2 ? 'bg-[var(--color-secondary)]' :
                'bg-surface-hover'
              }`}>
                {(player.display_name || '?').charAt(0).toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <div className={`font-semibold truncate ${index < 3 ? 'text-lg' : ''}`}>
                  {player.display_name}
                </div>
              </div>

              <div className="text-right shrink-0">
                <div className={`font-bold text-xl ${
                  index < 3 ? medalColors[index] : 'text-theme-text'
                }`}>
                  {player.total_score ?? 0}
                </div>
                <div className="text-xs text-theme-muted">points</div>
              </div>
            </div>
          ))}

          {ranked.length === 0 && (
            <div className="text-center text-theme-muted py-8">
              <p>No students joined yet.</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  /** Helper: render MCQ-style options with conditional answer reveal */
  const renderOptions = (options: string[], correctIdx: number | undefined, bodyText: string, mono?: boolean) => (
    <div className="grid grid-cols-1 gap-3">
      {options.map((option: string, idx: number) => {
        const isCorrect = correctIdx === idx
        const highlighted = shouldReveal && isCorrect
        return (
          <div
            key={idx}
            className={`flex items-center gap-4 px-6 py-4 rounded-xl border transition-colors ${
              highlighted
                ? 'border-[var(--color-success)] bg-[var(--color-success-subtle)] text-[var(--color-success)]'
                : 'border-theme-border bg-surface text-theme-text'
            }`}
          >
            <span className={`font-bold text-xl shrink-0 ${highlighted ? 'text-[var(--color-success)]' : 'text-theme-text-muted'}`}>
              {String.fromCharCode(65 + idx)}.
            </span>
            <span className={`flex-1 min-w-0 break-words${mono ? ' whitespace-pre-wrap font-mono' : ''} ${bodyText}`}>{option}</span>
            {highlighted && (
              <span className="ml-auto text-xs text-[var(--color-success)] font-semibold uppercase tracking-wide shrink-0">
                Correct
              </span>
            )}
          </div>
        )
      })}
    </div>
  )

  const renderContent = () => {
    if (!currentActivity) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-theme-muted py-24">
          <Monitor className="w-16 h-16 mb-4 opacity-40" />
          <p className="text-lg font-medium">No active activity</p>
          <p className="text-sm mt-1 text-theme-muted">Start an activity to display its content here</p>
        </div>
      )
    }

    const data = currentActivity.activity_data as any
    const medText = isFullscreen ? 'text-4xl' : 'text-2xl'
    const bodyText = isFullscreen ? 'text-xl' : 'text-base'

    switch (currentActivity.activity_type) {
      case 'pdf_slideshow': {
        if (!currentLesson?.id) {
          return (
            <div className="flex items-center justify-center h-full text-theme-text-muted">
              <p>Loading slide...</p>
            </div>
          )
        }
        const slideUrl = `/api/v1/lessons/${currentLesson.id}/activities/${currentActivity.id}/slides?page=${currentSlide}`
        return (
          <div className="flex items-center justify-center flex-1 p-4">
            <img
              key={currentSlide}
              src={slideUrl}
              alt={`Slide ${currentSlide}`}
              onContextMenu={(e) => e.preventDefault()}
              className={`max-w-full object-contain rounded-lg shadow-lg select-none pointer-events-none ${isFullscreen ? 'max-h-[90vh]' : 'max-h-[65vh]'}`}
            />
          </div>
        )
      }

      case 'google_slides': {
        const embedUrl = data?.embed_url
        if (!embedUrl) {
          return (
            <div className="flex items-center justify-center h-full text-theme-text-muted">
              <p>No Google Slides URL configured</p>
            </div>
          )
        }
        return (
          <div className="flex items-center justify-center flex-1 p-4">
            <div
              className="relative rounded-lg overflow-hidden aspect-video mx-auto"
              style={{
                // aspect-ratio needs an explicit width to compute height — without one the
                // element collapses to 0×0 and the absolute iframe renders blank.
                width: isFullscreen
                  ? `min(90vw, calc((100vh - 60px) * 16 / 9))`
                  : `min(75%, calc(55vh * 16 / 9))`,
              }}
              onContextMenu={(e) => e.preventDefault()}
            >
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                allowFullScreen
                loading="lazy"
                title="Google Slides"
              />
            </div>
          </div>
        )
      }

      case 'youtube_video': {
        const embedUrl = data?.embed_url
        if (!embedUrl) {
          return (
            <div className="flex items-center justify-center h-full text-theme-text-muted">
              <p>No YouTube URL configured</p>
            </div>
          )
        }
        return (
          <div className="flex items-center justify-center flex-1 p-4">
            <div
              className={`relative w-full rounded-lg overflow-hidden pb-[56.25%] ${isFullscreen ? 'max-w-[90vw]' : 'max-w-[75%]'}`}
              onContextMenu={(e) => e.preventDefault()}
            >
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                title="YouTube Video"
              />
            </div>
          </div>
        )
      }

      case 'mcq': {
        const options: string[] = data?.options || []
        const correctAnswer: number | undefined = data?.correct_answer
        return (
          <div className="max-w-4xl mx-auto w-full px-6 py-8">
            {(data?.category || data?.difficulty) && (
              <div className="flex items-center gap-2 mb-4">
                {data.category && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                    {data.category}
                  </span>
                )}
                {data.difficulty && <DifficultyBadge difficulty={data.difficulty} />}
              </div>
            )}
            <p className={`font-bold mb-8 leading-tight text-green-500 ${medText}`}>
              {data?.question || currentActivity.title}
            </p>
            {data?.question_image_url && (
              <div className="mb-8 flex justify-center">
                <img src={data.question_image_url} alt="" className="max-h-[40vh] rounded-lg object-contain" />
              </div>
            )}
            {renderOptions(options, correctAnswer, bodyText)}
          </div>
        )
      }

      case 'command_shell': {
        return (
          <div className="max-w-4xl mx-auto w-full px-6 py-8">
            {(data?.category || data?.difficulty) && (
              <div className="flex items-center gap-2 mb-4">
                {data.category && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                    {data.category}
                  </span>
                )}
                {data.difficulty && <DifficultyBadge difficulty={data.difficulty} />}
              </div>
            )}
            {shouldReveal && (
              <div className="mb-4 space-y-2">
                {data?.correct_answer && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-theme-text-muted text-xs uppercase tracking-wide">Expected Answer</span>
                    <span className="px-3 py-1 bg-[var(--color-success-subtle)] text-[var(--color-success)] font-mono rounded text-sm">
                      {data.correct_answer}
                    </span>
                  </div>
                )}
              </div>
            )}
            <ShellActivity
              activityData={data || {}}
              disabled={false}
              hideAnswerSection
              onExecuteCommand={async (command) => {
                try {
                  const res = await apiClient.executeShellCommandHost(currentLesson!.id, currentActivity!.id, command)
                  return { success: res.success, data: res.data, error: (res as any).error }
                } catch (err: any) {
                  return { success: false, error: err?.response?.data?.error || err?.message || 'Command failed' }
                }
              }}
              onSubmitAnswer={async () => ({ success: true })}
            />
          </div>
        )
      }

      case 'coding': {
        return (
          <div className="max-w-4xl mx-auto w-full px-6 py-8">
            {(data?.category || data?.difficulty) && (
              <div className="flex items-center gap-2 mb-4">
                {data.category && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                    {data.category}
                  </span>
                )}
                {data.difficulty && <DifficultyBadge difficulty={data.difficulty} />}
              </div>
            )}
            <BlocklyActivity
              activity={currentActivity}
              disabled={false}
              hideSubmit
              onSubmit={() => {}}
            />
          </div>
        )
      }

      case 'cyber_hygiene_quiz':
      case 'coding_quiz': {
        const options: string[] = data?.options || []
        const correctAnswer: number | undefined = data?.correct_answer
        return (
          <div className="max-w-4xl mx-auto w-full px-6 py-8">
            {(data?.category || data?.difficulty) && (
              <div className="flex items-center gap-2 mb-4">
                {data.category && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                    {data.category}
                  </span>
                )}
                {data.difficulty && <DifficultyBadge difficulty={data.difficulty} />}
              </div>
            )}
            <p className={`font-bold mb-8 leading-tight whitespace-pre-wrap text-green-500 ${medText}`}>
              {data?.question || currentActivity.title}
            </p>
            {renderOptions(options, correctAnswer, bodyText, true)}
            {shouldReveal && (data?.explanation || data?.explanation_image_url) && (
              <div className="mt-6 p-4 bg-surface border border-theme-border rounded-xl">
                <span className="text-xs text-theme-muted font-semibold uppercase tracking-wide">Explanation</span>
                {data?.explanation && <p className="text-theme-text mt-1 text-sm leading-relaxed">{data.explanation}</p>}
                {data?.explanation_image_url && <img src={data.explanation_image_url} alt="" className="mt-3 max-w-full max-h-64 rounded-lg object-contain" />}
              </div>
            )}
          </div>
        )
      }

      case 'packet_analysis': {
        const options: string[] = data?.options || []
        const correctAnswer: number | undefined = data?.correct_answer
        const packets: Array<{ no: number; time: string; source: string; destination: string; protocol: string; length: number; info: string }> = data?.packets || []
        return (
          <div className="max-w-5xl mx-auto w-full px-6 py-8">
            {(data?.category || data?.difficulty) && (
              <div className="flex items-center gap-2 mb-4">
                {data.category && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                    {data.category}
                  </span>
                )}
                {data.difficulty && <DifficultyBadge difficulty={data.difficulty} />}
              </div>
            )}

            {/* Packet table */}
            {packets.length > 0 && (
              <div className="mb-6 overflow-x-auto rounded-lg border border-theme-border">
                <table className="w-full text-sm font-mono">
                  <thead className="bg-surface-hover">
                    <tr>
                      <th className="px-3 py-2 text-left text-theme-text-muted font-medium">No.</th>
                      <th className="px-3 py-2 text-left text-theme-text-muted font-medium">Time</th>
                      <th className="px-3 py-2 text-left text-theme-text-muted font-medium">Source</th>
                      <th className="px-3 py-2 text-left text-theme-text-muted font-medium">Destination</th>
                      <th className="px-3 py-2 text-left text-theme-text-muted font-medium">Protocol</th>
                      <th className="px-3 py-2 text-left text-theme-text-muted font-medium">Len</th>
                      <th className="px-3 py-2 text-left text-theme-text-muted font-medium">Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packets.map((pkt, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-surface-deep' : 'bg-surface-deep/50'}>
                        <td className="px-3 py-1.5 text-theme-text-muted">{pkt.no}</td>
                        <td className="px-3 py-1.5 text-theme-text">{pkt.time}</td>
                        <td className="px-3 py-1.5 text-[var(--color-primary)]">{pkt.source}</td>
                        <td className="px-3 py-1.5 text-[var(--color-primary)]">{pkt.destination}</td>
                        <td className="px-3 py-1.5 text-[var(--color-warning)]">{pkt.protocol}</td>
                        <td className="px-3 py-1.5 text-theme-text-muted">{pkt.length}</td>
                        <td className="px-3 py-1.5 text-theme-text truncate max-w-[300px]">{pkt.info}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className={`font-bold mb-6 leading-tight text-green-500 ${medText}`}>
              {data?.question || currentActivity.title}
            </p>
            {renderOptions(options, correctAnswer, bodyText)}
            {shouldReveal && (data?.explanation || data?.explanation_image_url) && (
              <div className="mt-6 p-4 bg-surface border border-theme-border rounded-xl">
                <span className="text-xs text-theme-muted font-semibold uppercase tracking-wide">Explanation</span>
                {data?.explanation && <p className="text-theme-text mt-1 text-sm leading-relaxed">{data.explanation}</p>}
                {data?.explanation_image_url && <img src={data.explanation_image_url} alt="" className="mt-3 max-w-full max-h-64 rounded-lg object-contain" />}
              </div>
            )}
          </div>
        )
      }

      case 'phishing_quiz': {
        const isPhishing = resolvePhishingAnswer(data) === 'phishing'
        return (
          <div className="max-w-4xl mx-auto w-full px-6 py-8">
            {(data?.category || data?.difficulty) && (
              <div className="flex items-center gap-2 mb-4">
                {data.category && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                    {data.category}
                  </span>
                )}
                {data.difficulty && <DifficultyBadge difficulty={data.difficulty} />}
              </div>
            )}

            {/* Email preview */}
            <div className="bg-white rounded-lg overflow-hidden mb-6">
              {data?.chromeBar && (
                <div className="bg-gray-200 px-4 py-2 text-xs text-theme-muted font-mono truncate">
                  {data.chromeBar}
                </div>
              )}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-theme-text font-bold text-sm shrink-0"
                    style={{ backgroundColor: data?.fromColor || '#6b7280' }}
                  >
                    {(data?.fromName || '?').charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{data?.fromName}</div>
                    <div className="text-sm text-theme-muted">{data?.fromEmail}</div>
                  </div>
                  {data?.date && (
                    <div className="ml-auto text-xs text-theme-text-muted">{data.date}</div>
                  )}
                </div>
                <div className="text-lg font-semibold text-gray-900">{data?.subject}</div>
              </div>
              {data?.body && (
                <div
                  className="px-6 py-4 text-gray-800 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: data.body }}
                />
              )}
            </div>

            {/* Answer reveal — only shown after shouldReveal */}
            {shouldReveal && (
              <>
                <div className={`p-4 rounded-lg border mb-4 ${
                  isPhishing
                    ? 'border-[var(--color-danger)] bg-[var(--color-danger-subtle)]'
                    : 'border-[var(--color-success)] bg-[var(--color-success-subtle)]'
                }`}>
                  <span className={`text-lg font-bold ${isPhishing ? 'text-[var(--color-danger)]' : 'text-[var(--color-success)]'}`}>
                    {isPhishing ? 'This is PHISHING' : 'This is LEGITIMATE'}
                  </span>
                </div>

                {data?.hints && data.hints.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-xs text-[var(--color-secondary)] font-semibold uppercase tracking-wide">Hints</span>
                    {data.hints.map((clue: string, i: number) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-theme-text">
                        <span className="text-[var(--color-secondary)] shrink-0 mt-0.5">•</span>
                        <span>{clue}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {!shouldReveal && (
              <div className="p-4 rounded-lg border border-theme-border bg-surface text-center">
                <span className="text-theme-text-muted">Is this email phishing or legitimate?</span>
              </div>
            )}
          </div>
        )
      }

      case 'python_repl': {
        return (
          <div className="max-w-4xl mx-auto w-full px-6 py-8">
            {(data?.category || data?.difficulty) && (
              <div className="flex items-center gap-2 mb-4">
                {data.category && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                    {data.category}
                  </span>
                )}
                {data.difficulty && <DifficultyBadge difficulty={data.difficulty} />}
              </div>
            )}
            <PythonReplActivity
              activityData={data || {}}
              disabled={false}
              hideSubmit
              onSubmitResponse={async () => ({ success: true })}
            />
          </div>
        )
      }

      case 'potato_pirates': {
        return (
          <div className="max-w-4xl mx-auto w-full px-4 py-4">
            <PotatoPiratesActivity
              activity={currentActivity}
              hideSubmit
              disabled
              onSubmit={() => {}}
            />
          </div>
        )
      }

      default: {
        return (
          <div className="max-w-4xl mx-auto w-full px-6 py-8 text-center">
            <p className={`font-bold mb-4 leading-tight text-green-500 ${medText}`}>
              {data?.question || data?.title || currentActivity.title}
            </p>
            {currentActivity.instruction && (
              <p className={`text-theme-text-muted leading-relaxed ${bodyText}`}>
                {currentActivity.instruction}
              </p>
            )}
          </div>
        )
      }
    }
  }

  const totalSlides = currentActivity?.activity_data?.total_slides ?? 1
  const isPdf = currentActivity?.activity_type === 'pdf_slideshow'

  return (
    <div
      ref={containerRef}
      className={`flex flex-col overflow-hidden ${
        isFullscreen
          ? 'fixed inset-0 bg-theme-bg z-50'
          : 'bg-surface-deep rounded-lg h-full'
      }`}
    >
      {/* Header bar */}
      <div
        className={`flex items-center justify-between px-4 py-3 shrink-0 border-b ${
          isFullscreen
            ? 'bg-chrome-bg border-theme-border'
            : 'bg-surface border-theme-border'
        }`}
      >
        {/* Activity title + type badge */}
        <div className="flex items-center gap-3 min-w-0">
          {currentActivity ? (
            <span
              className={`font-semibold truncate ${
                isFullscreen ? 'text-lg text-theme-text' : 'text-base text-theme-text'
              }`}
            >
              {currentActivity.title}
            </span>
          ) : (
            <span className="text-theme-text-muted text-sm">No active activity</span>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 shrink-0 ml-3">
          {/* Response count badge */}
          {isQuizType && currentActivity?.status === 'active' && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-hover text-sm">
              <Users className="w-3.5 h-3.5 text-[var(--color-primary)]" />
              <span className={`font-mono ${
                responseInfo.responded >= responseInfo.total && responseInfo.total > 0
                  ? 'text-[var(--color-success)]'
                  : 'text-theme-text'
              }`}>
                {responseInfo.responded}/{responseInfo.total}
              </span>
            </div>
          )}

          {/* Answer / Leaderboard toggle — shown when answers are revealed */}
          {isQuizType && shouldReveal && (
            <div className="flex items-center bg-surface-hover rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('question')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'question'
                    ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]'
                    : 'text-theme-text hover:text-theme-text'
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                Answer
              </button>
              <button
                onClick={() => setViewMode('leaderboard')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'leaderboard'
                    ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]'
                    : 'text-theme-text hover:text-theme-text'
                }`}
              >
                <Trophy className="w-3.5 h-3.5" />
                Leaderboard
              </button>
            </div>
          )}

          {/* PDF slide navigation */}
          {isPdf && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleChangeSlide(currentSlide - 1)}
                disabled={currentSlide <= 1}
                className="p-1.5 rounded-lg bg-surface-hover text-theme-text hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Previous slide"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-mono text-theme-text min-w-[4.5rem] text-center">
                {currentSlide} / {totalSlides}
              </span>
              <button
                onClick={() => handleChangeSlide(currentSlide + 1)}
                disabled={currentSlide >= totalSlides}
                className="p-1.5 rounded-lg bg-surface-hover text-theme-text hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Next slide"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Next/End activity — shown in fullscreen so the host can advance without exiting */}
          {isFullscreen && showAdvanceControls && (
            hasNextActivity ? (
              <button
                onClick={onNextActivity}
                disabled={advancing}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <SkipForward className="w-4 h-4" />}
                {advancing ? 'Starting...' : 'Next Activity'}
              </button>
            ) : (
              <button
                onClick={onEndActivity}
                disabled={advancing}
                className="ds-btn ds-btn-danger ds-btn-sm"
              >
                {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                {advancing ? 'Ending...' : 'End Activity'}
              </button>
            )
          )}

          {/* Fullscreen toggle */}
          <button
            onClick={handleToggleFullscreen}
            className="p-1.5 rounded-lg bg-surface-hover text-theme-text hover:bg-surface-hover transition-colors"
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div
        className="flex-1 flex flex-col justify-center overflow-auto text-theme-text"
      >
        {viewMode === 'leaderboard' && shouldReveal ? renderLeaderboard() : renderContent()}
      </div>
    </div>
  )
}
